import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChromePicker, ColorResult } from 'react-color';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Loader2, X, Save } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { showToast } from '@/services/toast.service';
import TenantConfigService from '@/services/tenantConfig.service';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useQuery } from '@tanstack/react-query';
import { S3Uploader, UploadedFile } from '@/components/s3uploader/S3Uploader';
import { useRef } from 'react';

interface ColorPickerFieldProps {
  label: string;
  color: string;
  onChange: (color: string) => void;
}

function ColorPickerField({ label, color, onChange }: ColorPickerFieldProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="relative">
        <button
          type="button"
          className="flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors"
          onClick={() => setOpen(!open)}
        >
          <div
            className="w-6 h-6 rounded border border-border"
            style={{ backgroundColor: color }}
          />
          <span className="font-mono text-muted-foreground">{color}</span>
        </button>
        {open && (
          <div ref={popoverRef} className="absolute z-50 mt-2">
            <ChromePicker
              color={color}
              onChange={(c: ColorResult) => onChange(c.hex)}
              disableAlpha
            />
          </div>
        )}
      </div>
    </div>
  );
}

// CSS defaults from index.css (:root light theme)
const DEFAULTS: Record<string, string> = {
  primary_color: '#5a3ed8',
  secondary_color: '#fef6e6',
  chart_1_color: '#e05a2b',
  chart_2_color: '#2a9d8f',
  chart_3_color: '#3d5a80',
  chart_4_color: '#e9c46a',
  chart_5_color: '#d4a843',
};

const COLOR_FIELDS: { key: string; label: string }[] = [
  { key: 'primary_color', label: 'Primary' },
  { key: 'secondary_color', label: 'Secondary' },
  { key: 'chart_1_color', label: 'Chart 1' },
  { key: 'chart_2_color', label: 'Chart 2' },
  { key: 'chart_3_color', label: 'Chart 3' },
  { key: 'chart_4_color', label: 'Chart 4' },
  { key: 'chart_5_color', label: 'Chart 5' },
];

const FONT_FILE_PATTERN = /\.(woff2?|ttf|otf|eot)(\?.*)?$/i;

const CARD_HEADER_DEFAULT = '#e5e5e5';
const CARD_TITLE_DEFAULT = '#0a0f1a';
const PAGE_BG_DEFAULT = '#fcfcfd';
const SIDEBAR_BG_DEFAULT = '#0f0e31';
const SIDEBAR_ACCENT_DEFAULT = '#ff9f00';
const CARD_BG_DEFAULT = '#ffffff';

export default function WhiteLabelTab() {
  const queryClient = useQueryClient();
  const { data: tenantConfig, isLoading: configLoading } = useTenantConfig();

  const { data: registrationData, isLoading: regLoading } = useQuery({
    queryKey: ['tenantRegistration'],
    queryFn: async () => {
      return await TenantConfigService.getInstance().getTenantRegistration();
    },
    staleTime: 5 * 60 * 1000,
  });

  const tenantConfigId = registrationData?.tenant_config?.id || '';

  const [colors, setColors] = useState<Record<string, string>>({ ...DEFAULTS });
  const [pageTitle, setPageTitle] = useState('');
  const [whitelabelEnabled, setWhitelabelEnabled] = useState(true);
  const [cardHeaderBgColor, setCardHeaderBgColor] = useState('');
  const [cardTitleColor, setCardTitleColor] = useState('');
  const [hideSourceIcons, setHideSourceIcons] = useState(false);
  const [pageBgColor, setPageBgColor] = useState('');
  const [sidebarBgColor, setSidebarBgColor] = useState('');
  const [sidebarAccentColor, setSidebarAccentColor] = useState('');
  const [cardBgColor, setCardBgColor] = useState('');
  const [uiFontUrl, setUiFontUrl] = useState('');
  const [uiFontFamily, setUiFontFamily] = useState('');
  const [uiFontSource, setUiFontSource] = useState<'url' | 'upload'>('url');
  const [metricFontUrl, setMetricFontUrl] = useState('');
  const [metricFontFamily, setMetricFontFamily] = useState('');
  const [metricFontSource, setMetricFontSource] = useState<'url' | 'upload'>('url');
  const [initialized, setInitialized] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Track the last-saved colors so we can compute a minimal diff
  const savedColorsRef = useRef<Record<string, string>>({ ...DEFAULTS });
  const savedPageTitleRef = useRef<string>('');
  const savedCardHeaderBgRef = useRef<string | null>(null);
  const savedCardTitleColorRef = useRef<string | null>(null);
  const savedPageBgColorRef = useRef<string | null>(null);
  const savedSidebarBgColorRef = useRef<string | null>(null);
  const savedSidebarAccentColorRef = useRef<string | null>(null);
  const savedCardBgColorRef = useRef<string | null>(null);
  const savedUiFontUrlRef = useRef<string | null>(null);
  const savedUiFontFamilyRef = useRef<string | null>(null);
  const savedMetricFontUrlRef = useRef<string | null>(null);
  const savedMetricFontFamilyRef = useRef<string | null>(null);

  // Initialize from tenant config
  useEffect(() => {
    if (tenantConfig?.whitelabeling && !initialized) {
      const wl = tenantConfig.whitelabeling;
      const updated = { ...DEFAULTS };
      for (const { key } of COLOR_FIELDS) {
        const val = wl[key as keyof typeof wl] as string | undefined;
        if (val) updated[key] = val;
      }
      setColors(updated);
      savedColorsRef.current = { ...updated };
      setPageTitle(wl.page_title || '');
      savedPageTitleRef.current = wl.page_title || '';

      // Card header bg: null = default, hex = custom color
      const savedCardHeaderBg = (wl as Record<string, any>).card_header_bg_color as string | undefined;
      savedCardHeaderBgRef.current = savedCardHeaderBg || null;
      if (savedCardHeaderBg && savedCardHeaderBg !== 'transparent') {
        setCardHeaderBgColor(savedCardHeaderBg);
      }

      // Card title color
      const savedCardTitleColor = (wl as Record<string, any>).card_title_color as string | undefined;
      savedCardTitleColorRef.current = savedCardTitleColor || null;
      if (savedCardTitleColor) {
        setCardTitleColor(savedCardTitleColor);
      }

      // Hide source icons (defaults to false)
      setHideSourceIcons(wl.hide_source_icons === true);

      // Page background color
      const savedPageBg = (wl as Record<string, any>).page_bg_color as string | undefined;
      savedPageBgColorRef.current = savedPageBg || null;
      if (savedPageBg) setPageBgColor(savedPageBg);

      // Sidebar background color
      const savedSidebarBg = (wl as Record<string, any>).sidebar_bg_color as string | undefined;
      savedSidebarBgColorRef.current = savedSidebarBg || null;
      if (savedSidebarBg) setSidebarBgColor(savedSidebarBg);

      // Sidebar accent color
      const savedSidebarAccent = (wl as Record<string, any>).sidebar_accent_color as string | undefined;
      savedSidebarAccentColorRef.current = savedSidebarAccent || null;
      if (savedSidebarAccent) setSidebarAccentColor(savedSidebarAccent);

      // Card background color
      const savedCardBg = (wl as Record<string, any>).card_bg_color as string | undefined;
      savedCardBgColorRef.current = savedCardBg || null;
      if (savedCardBg) setCardBgColor(savedCardBg);

      // Font customization
      const savedUiFontUrl = (wl as Record<string, any>).ui_font_url as string | undefined;
      const savedUiFontFamily = (wl as Record<string, any>).ui_font_family as string | undefined;
      const savedMetricFontUrl = (wl as Record<string, any>).metric_font_url as string | undefined;
      const savedMetricFontFamily = (wl as Record<string, any>).metric_font_family as string | undefined;
      savedUiFontUrlRef.current = savedUiFontUrl || null;
      savedUiFontFamilyRef.current = savedUiFontFamily || null;
      savedMetricFontUrlRef.current = savedMetricFontUrl || null;
      savedMetricFontFamilyRef.current = savedMetricFontFamily || null;
      if (savedUiFontUrl) {
        setUiFontUrl(savedUiFontUrl);
        setUiFontSource(FONT_FILE_PATTERN.test(savedUiFontUrl) ? 'upload' : 'url');
      }
      if (savedUiFontFamily) setUiFontFamily(savedUiFontFamily);
      if (savedMetricFontUrl) {
        setMetricFontUrl(savedMetricFontUrl);
        setMetricFontSource(FONT_FILE_PATTERN.test(savedMetricFontUrl) ? 'upload' : 'url');
      }
      if (savedMetricFontFamily) setMetricFontFamily(savedMetricFontFamily);

      // Default enabled to true if there are any saved whitelabel settings
      if (wl.enabled === false) {
        setWhitelabelEnabled(false);
      } else {
        const hasAnySettings = !!(
          wl.logo_path || wl.favicon_path || wl.page_title ||
          COLOR_FIELDS.some(({ key }) => wl[key as keyof typeof wl])
        );
        setWhitelabelEnabled(wl.enabled === true || hasAnySettings);
      }

      setInitialized(true);
    }
  }, [tenantConfig, initialized]);

  const setColor = useCallback((key: string, value: string) => {
    setColors((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  const handlePageTitleChange = useCallback((value: string) => {
    setPageTitle(value);
    setDirty(true);
  }, []);

  const handleToggleEnabled = useCallback(async (checked: boolean) => {
    if (!tenantConfigId) return;
    setWhitelabelEnabled(checked);
    try {
      await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, {
        enabled: checked,
      });
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
      showToast.success(checked ? 'White labeling enabled' : 'White labeling disabled');
    } catch (err: any) {
      setWhitelabelEnabled(!checked);
      showToast.error('Failed to update setting', {
        description: err.message || 'An error occurred',
      });
    }
  }, [tenantConfigId, queryClient]);

  const handleToggleHideSourceIcons = useCallback(async (checked: boolean) => {
    if (!tenantConfigId) return;
    setHideSourceIcons(checked);
    try {
      await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, {
        hide_source_icons: checked,
      });
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
      showToast.success(checked ? 'Source icons hidden' : 'Source icons shown');
    } catch (err: any) {
      setHideSourceIcons(!checked);
      showToast.error('Failed to update setting', {
        description: err.message || 'An error occurred',
      });
    }
  }, [tenantConfigId, queryClient]);

  const saveChanges = useCallback(async () => {
    if (!tenantConfigId || !initialized) return;
    setSaving(true);
    try {
      // Save changed colors
      const changedColors: Record<string, string> = {};
      for (const { key } of COLOR_FIELDS) {
        if (colors[key] !== savedColorsRef.current[key]) {
          changedColors[key] = colors[key];
        }
      }
      if (Object.keys(changedColors).length > 0) {
        await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, changedColors);
        savedColorsRef.current = { ...colors };
      }

      // Save changed page title
      if (pageTitle !== savedPageTitleRef.current) {
        await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, {
          page_title: pageTitle || null,
        });
        savedPageTitleRef.current = pageTitle;
      }

      // Save card header bg color
      const newCardHeaderBg = cardHeaderBgColor || null;
      if (newCardHeaderBg !== savedCardHeaderBgRef.current) {
        await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, {
          card_header_bg_color: newCardHeaderBg,
        });
        savedCardHeaderBgRef.current = newCardHeaderBg;
      }

      // Save card title color
      const newCardTitleColor = cardTitleColor || null;
      if (newCardTitleColor !== savedCardTitleColorRef.current) {
        await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, {
          card_title_color: newCardTitleColor,
        });
        savedCardTitleColorRef.current = newCardTitleColor;
      }

      // Save page background color
      const newPageBg = pageBgColor || null;
      if (newPageBg !== savedPageBgColorRef.current) {
        await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, {
          page_bg_color: newPageBg,
        });
        savedPageBgColorRef.current = newPageBg;
      }

      // Save sidebar background color
      const newSidebarBg = sidebarBgColor || null;
      if (newSidebarBg !== savedSidebarBgColorRef.current) {
        await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, {
          sidebar_bg_color: newSidebarBg,
        });
        savedSidebarBgColorRef.current = newSidebarBg;
      }

      // Save sidebar accent color
      const newSidebarAccent = sidebarAccentColor || null;
      if (newSidebarAccent !== savedSidebarAccentColorRef.current) {
        await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, {
          sidebar_accent_color: newSidebarAccent,
        });
        savedSidebarAccentColorRef.current = newSidebarAccent;
      }

      // Save card background color
      const newCardBg = cardBgColor || null;
      if (newCardBg !== savedCardBgColorRef.current) {
        await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, {
          card_bg_color: newCardBg,
        });
        savedCardBgColorRef.current = newCardBg;
      }

      // Save font settings
      const fontPayload: Record<string, string | null> = {};
      if ((uiFontUrl || null) !== savedUiFontUrlRef.current) fontPayload.ui_font_url = uiFontUrl || null;
      if ((uiFontFamily || null) !== savedUiFontFamilyRef.current) fontPayload.ui_font_family = uiFontFamily || null;
      if ((metricFontUrl || null) !== savedMetricFontUrlRef.current) fontPayload.metric_font_url = metricFontUrl || null;
      if ((metricFontFamily || null) !== savedMetricFontFamilyRef.current) fontPayload.metric_font_family = metricFontFamily || null;
      if (Object.keys(fontPayload).length > 0) {
        await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, fontPayload);
        savedUiFontUrlRef.current = uiFontUrl || null;
        savedUiFontFamilyRef.current = uiFontFamily || null;
        savedMetricFontUrlRef.current = metricFontUrl || null;
        savedMetricFontFamilyRef.current = metricFontFamily || null;
      }

      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
      setDirty(false);
      showToast.success('Settings saved');
    } catch (err: any) {
      showToast.error('Failed to save settings', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSaving(false);
    }
  }, [tenantConfigId, initialized, colors, pageTitle, cardHeaderBgColor, cardTitleColor, pageBgColor, sidebarBgColor, sidebarAccentColor, cardBgColor, uiFontUrl, uiFontFamily, metricFontUrl, metricFontFamily, queryClient]);

  const restoreDefaults = useCallback(async () => {
    if (!tenantConfigId) return;
    try {
      const nullColors: Record<string, null> = {};
      for (const { key } of COLOR_FIELDS) {
        nullColors[key] = null;
      }
      nullColors['card_header_bg_color'] = null;
      nullColors['card_title_color'] = null;
      nullColors['page_bg_color'] = null;
      nullColors['sidebar_bg_color'] = null;
      nullColors['sidebar_accent_color'] = null;
      nullColors['card_bg_color'] = null;
      nullColors['ui_font_url'] = null;
      nullColors['ui_font_family'] = null;
      nullColors['metric_font_url'] = null;
      nullColors['metric_font_family'] = null;
      await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, nullColors);
      setColors({ ...DEFAULTS });
      savedColorsRef.current = { ...DEFAULTS };
      setCardHeaderBgColor('');
      savedCardHeaderBgRef.current = null;
      setCardTitleColor('');
      savedCardTitleColorRef.current = null;
      setPageBgColor('');
      savedPageBgColorRef.current = null;
      setSidebarBgColor('');
      savedSidebarBgColorRef.current = null;
      setSidebarAccentColor('');
      savedSidebarAccentColorRef.current = null;
      setCardBgColor('');
      savedCardBgColorRef.current = null;
      setUiFontUrl('');
      setUiFontFamily('');
      setMetricFontUrl('');
      setMetricFontFamily('');
      savedUiFontUrlRef.current = null;
      savedUiFontFamilyRef.current = null;
      savedMetricFontUrlRef.current = null;
      savedMetricFontFamilyRef.current = null;
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
      showToast.success('Colors restored to defaults');
    } catch (err: any) {
      showToast.error('Failed to restore defaults', {
        description: err.message || 'An error occurred',
      });
    }
  }, [tenantConfigId, queryClient]);

  const handleLogoUpload = useCallback(
    async (file: UploadedFile) => {
      if (!tenantConfigId) return;
      try {
        await TenantConfigService.getInstance().saveWhitelabelingLogo(
          tenantConfigId,
          file.id
        );
        queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
        showToast.success('Logo updated');
      } catch (err: any) {
        showToast.error('Failed to save logo', {
          description: err.message || 'An error occurred',
        });
      }
    },
    [tenantConfigId, queryClient]
  );

  const removeLogo = useCallback(async () => {
    if (!tenantConfigId) return;
    try {
      await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, {
        logo_path: null,
      });
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
      showToast.success('Logo removed');
    } catch (err: any) {
      showToast.error('Failed to remove logo', {
        description: err.message || 'An error occurred',
      });
    }
  }, [tenantConfigId, queryClient]);

  const handleFaviconUpload = useCallback(
    async (file: UploadedFile) => {
      if (!tenantConfigId) return;
      try {
        await TenantConfigService.getInstance().saveWhitelabelingFavicon(
          tenantConfigId,
          file.id
        );
        queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
        showToast.success('Favicon updated');
      } catch (err: any) {
        showToast.error('Failed to save favicon', {
          description: err.message || 'An error occurred',
        });
      }
    },
    [tenantConfigId, queryClient]
  );

  const removeFavicon = useCallback(async () => {
    if (!tenantConfigId) return;
    try {
      await TenantConfigService.getInstance().saveWhitelabeling(tenantConfigId, {
        favicon_path: null,
      });
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
      showToast.success('Favicon removed');
    } catch (err: any) {
      showToast.error('Failed to remove favicon', {
        description: err.message || 'An error occurred',
      });
    }
  }, [tenantConfigId, queryClient]);

  const handleFontUpload = useCallback(
    async (slot: 'ui' | 'metric', file: UploadedFile) => {
      if (!tenantConfigId) return;
      try {
        await TenantConfigService.getInstance().saveWhitelabelingFont(tenantConfigId, slot, file.id);
        await new Promise((resolve) => setTimeout(resolve, 3000));
        queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
        showToast.success(`${slot === 'ui' ? 'UI' : 'Metric'} font uploaded`);
      } catch (err: any) {
        showToast.error('Failed to upload font', {
          description: err.message || 'An error occurred',
        });
      }
    },
    [tenantConfigId, queryClient]
  );

  if (configLoading || regLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Enable/Disable Toggle Card */}
      <Card>
        <CardContent className="flex items-center justify-between py-4">
          <div>
            <p className="font-medium">Enable White Labeling</p>
            <p className="text-sm text-muted-foreground">
              When disabled, all default Pliable branding is used. Your settings are preserved and can be re-enabled at any time.
            </p>
          </div>
          <Switch
            checked={whitelabelEnabled}
            onCheckedChange={handleToggleEnabled}
          />
        </CardContent>
      </Card>

      {/* Theme Colors Card */}
      <Card className={!whitelabelEnabled ? 'opacity-50 pointer-events-none' : ''}>
        <CardHeader>
          <CardTitle>Theme Colors</CardTitle>
          <CardDescription>
            Customize the primary, secondary, and chart colors used throughout the application
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {COLOR_FIELDS.slice(0, 2).map(({ key, label }) => (
              <ColorPickerField
                key={key}
                label={label}
                color={colors[key]}
                onChange={(v) => setColor(key, v)}
              />
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {COLOR_FIELDS.slice(2).map(({ key, label }) => (
              <ColorPickerField
                key={key}
                label={label}
                color={colors[key]}
                onChange={(v) => setColor(key, v)}
              />
            ))}
          </div>
          {/* Page & Sidebar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t">
            <ColorPickerField
              label="Page Background"
              color={pageBgColor || PAGE_BG_DEFAULT}
              onChange={(v) => {
                setPageBgColor(v);
                setDirty(true);
              }}
            />
            <ColorPickerField
              label="Sidebar Background"
              color={sidebarBgColor || SIDEBAR_BG_DEFAULT}
              onChange={(v) => {
                setSidebarBgColor(v);
                setDirty(true);
              }}
            />
            <ColorPickerField
              label="Sidebar Accent"
              color={sidebarAccentColor || SIDEBAR_ACCENT_DEFAULT}
              onChange={(v) => {
                setSidebarAccentColor(v);
                setDirty(true);
              }}
            />
          </div>
          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t">
            <ColorPickerField
              label="Card Background"
              color={cardBgColor || CARD_BG_DEFAULT}
              onChange={(v) => {
                setCardBgColor(v);
                setDirty(true);
              }}
            />
            <ColorPickerField
              label="Card Title Color"
              color={cardTitleColor || CARD_TITLE_DEFAULT}
              onChange={(v) => {
                setCardTitleColor(v);
                setDirty(true);
              }}
            />
            <ColorPickerField
              label="Card Header BG"
              color={cardHeaderBgColor || CARD_HEADER_DEFAULT}
              onChange={(v) => {
                setCardHeaderBgColor(v);
                setDirty(true);
              }}
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={saveChanges} disabled={!dirty || saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
            <Button variant="outline" onClick={restoreDefaults}>
              Restore Defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Custom Fonts Card */}
      <Card className={!whitelabelEnabled ? 'opacity-50 pointer-events-none' : ''}>
        <CardHeader>
          <CardTitle>Custom Fonts</CardTitle>
          <CardDescription>
            Override the default UI font and/or the large metric display font. Provide a Google Fonts URL or upload a font file.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* UI Font */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">UI Font</Label>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className={uiFontSource === 'url' ? 'font-medium text-foreground' : ''}>URL</span>
                <Switch
                  checked={uiFontSource === 'upload'}
                  onCheckedChange={(checked) => {
                    setUiFontSource(checked ? 'upload' : 'url');
                    setUiFontUrl('');
                    setDirty(true);
                  }}
                />
                <span className={uiFontSource === 'upload' ? 'font-medium text-foreground' : ''}>Upload</span>
              </div>
            </div>
            {uiFontSource === 'url' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Stylesheet URL</Label>
                  <Input
                    placeholder="https://fonts.googleapis.com/css2?family=..."
                    value={uiFontUrl}
                    onChange={(e) => { setUiFontUrl(e.target.value); setDirty(true); }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Font Family</Label>
                  <Input
                    placeholder="Roboto"
                    value={uiFontFamily}
                    onChange={(e) => { setUiFontFamily(e.target.value); setDirty(true); }}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {uiFontUrl && FONT_FILE_PATTERN.test(uiFontUrl) && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Uploaded: {uiFontUrl.split('/').pop()?.split('?')[0]}</span>
                    <Button variant="outline" size="sm" onClick={() => {
                      setUiFontUrl('');
                      setDirty(true);
                    }}>
                      <X className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                )}
                <S3Uploader
                  onUploadComplete={(file) => handleFontUpload('ui', file)}
                  label="Drag and drop a font file, or click to browse"
                  accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf"
                />
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Font Family</Label>
                  <Input
                    placeholder="Roboto"
                    value={uiFontFamily}
                    onChange={(e) => { setUiFontFamily(e.target.value); setDirty(true); }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Metric Font */}
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Metric Display Font</Label>
                <p className="text-xs text-muted-foreground">Used for the large number in Single Value charts</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className={metricFontSource === 'url' ? 'font-medium text-foreground' : ''}>URL</span>
                <Switch
                  checked={metricFontSource === 'upload'}
                  onCheckedChange={(checked) => {
                    setMetricFontSource(checked ? 'upload' : 'url');
                    setMetricFontUrl('');
                    setDirty(true);
                  }}
                />
                <span className={metricFontSource === 'upload' ? 'font-medium text-foreground' : ''}>Upload</span>
              </div>
            </div>
            {metricFontSource === 'url' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Stylesheet URL</Label>
                  <Input
                    placeholder="https://fonts.googleapis.com/css2?family=..."
                    value={metricFontUrl}
                    onChange={(e) => { setMetricFontUrl(e.target.value); setDirty(true); }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Font Family</Label>
                  <Input
                    placeholder="Oswald"
                    value={metricFontFamily}
                    onChange={(e) => { setMetricFontFamily(e.target.value); setDirty(true); }}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {metricFontUrl && FONT_FILE_PATTERN.test(metricFontUrl) && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Uploaded: {metricFontUrl.split('/').pop()?.split('?')[0]}</span>
                    <Button variant="outline" size="sm" onClick={() => {
                      setMetricFontUrl('');
                      setDirty(true);
                    }}>
                      <X className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                )}
                <S3Uploader
                  onUploadComplete={(file) => handleFontUpload('metric', file)}
                  label="Drag and drop a font file, or click to browse"
                  accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf"
                />
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Font Family</Label>
                  <Input
                    placeholder="Oswald"
                    value={metricFontFamily}
                    onChange={(e) => { setMetricFontFamily(e.target.value); setDirty(true); }}
                  />
                </div>
              </div>
            )}
          </div>
          <Button onClick={saveChanges} disabled={!dirty || saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save
          </Button>
        </CardContent>
      </Card>

      {/* Hide Source Icons Toggle Card */}
      <Card className={!whitelabelEnabled ? 'opacity-50 pointer-events-none' : ''}>
        <CardContent className="flex items-center justify-between py-4">
          <div>
            <p className="font-medium">Hide Source Icons</p>
            <p className="text-sm text-muted-foreground">
              Hides Source Icons on Charts.
            </p>
          </div>
          <Switch
            checked={hideSourceIcons}
            onCheckedChange={handleToggleHideSourceIcons}
          />
        </CardContent>
      </Card>

      {/* Custom Logo Card */}
      <Card className={!whitelabelEnabled ? 'opacity-50 pointer-events-none' : ''}>
        <CardHeader>
          <CardTitle>Custom Logo</CardTitle>
          <CardDescription>
            Upload a custom logo to replace the default branding
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {tenantConfig?.whitelabeling?.logo_path && (
            <div className="space-y-2">
              <img
                src={tenantConfig.whitelabeling.logo_path}
                alt="Custom logo"
                className="max-h-16 object-contain"
              />
              <Button variant="outline" size="sm" onClick={removeLogo}>
                <X className="h-4 w-4 mr-1" />
                Remove Custom Logo
              </Button>
            </div>
          )}
          <S3Uploader
            onUploadComplete={handleLogoUpload}
            label="Drag and drop a logo image, or click to browse"
          />
        </CardContent>
      </Card>

      {/* Custom Favicon Card */}
      <Card className={!whitelabelEnabled ? 'opacity-50 pointer-events-none' : ''}>
        <CardHeader>
          <CardTitle>Custom Favicon</CardTitle>
          <CardDescription>
            Upload a custom favicon to replace the default browser tab icon
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {tenantConfig?.whitelabeling?.favicon_path && (
            <div className="space-y-2">
              <img
                src={tenantConfig.whitelabeling.favicon_path}
                alt="Custom favicon"
                className="h-8 w-8 object-contain"
              />
              <Button variant="outline" size="sm" onClick={removeFavicon}>
                <X className="h-4 w-4 mr-1" />
                Remove Custom Favicon
              </Button>
            </div>
          )}
          <S3Uploader
            onUploadComplete={handleFaviconUpload}
            label="Drag and drop a favicon image, or click to browse"
          />
        </CardContent>
      </Card>

      {/* Page Title Card */}
      <Card className={!whitelabelEnabled ? 'opacity-50 pointer-events-none' : ''}>
        <CardHeader>
          <CardTitle>Page Title</CardTitle>
          <CardDescription>
            Set a custom page title shown in the browser tab
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            placeholder="Pliable"
            value={pageTitle}
            onChange={(e) => handlePageTitleChange(e.target.value)}
          />
          <Button onClick={saveChanges} disabled={!dirty || saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
